import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../../layouts/user';
import UserHead from '../../components/user/head';
import { WarnAlert } from '../../components/user/alert';
import WalletNav from '../../components/wallet/nav';
import TransactionRow from '../../components/transaction/row';

import InvestmentTransaction from '../../components/transaction/investment';
import DepositTransaction from '../../components/transaction/deposit';
import WithdrawalTransaction from '../../components/transaction/withdrawal';
import RedemptionTransaction from '../../components/transaction/redemption';
import SwapTransaction from '../../components/transaction/swap';

import Dialog from '../../components/common/dialog';
import { post } from '../../libs/api';
import ReactPaginate from 'react-paginate';

const PAGE_SIZE = 10;

// Sent as `orderByColumn` / `isAsc` (authentication-service WalletController ->
// backend-api PubWalletController). Both are currently ignored — see the note on
// sortByNewestFirst — but they are kept so the request still states its intent.
const SORT_FIELD = 'p.create_time';
const SORT_ORDER = 'desc';

// /wallet/transaction returns the client's ENTIRE history, in join order, with
// no ORDER BY: backend-api depends on pagehelper-spring-boot-starter 1.4.3, whose
// autoconfiguration is registered only through META-INF/spring.factories, and
// Spring Boot 3.5 ignores that file. PageInterceptor is therefore never
// registered and PageHelper.startPage() is a no-op, so neither the LIMIT nor the
// injected ORDER BY ever reaches the query. Ordering and paging both have to
// happen here until that is fixed server-side (PB-1260).
//
// create_datetime is serialised as "yyyy-MM-dd HH:mm:ss" and is mandatory on
// every row, so a plain string comparison is chronological. Array#sort is
// stable, so equal timestamps keep the order the backend sent them in.
const sortByNewestFirst = (a, b) => {
  const left = a?.create_datetime ?? '';
  const right = b?.create_datetime ?? '';
  if (left === right) return 0;
  return left < right ? 1 : -1;
};

function Page() {
  const [active, setActive] = useState('all');
  const [allTransactions, setAllTransactions] = useState([]);
  const [transaction, setTransaction] = useState(null);
  const [pageOffset, setPageOffset] = useState(0);
  const [loadFailed, setLoadFailed] = useState(false);

  // Paging is derived, not stored, so it cannot drift out of sync with the rows.
  const pageCount = Math.ceil(allTransactions.length / PAGE_SIZE);
  const startIndex = pageOffset * PAGE_SIZE;
  const transactions = allTransactions.slice(
    startIndex,
    startIndex + PAGE_SIZE
  );

  const navs = [
    { key: 'all', text: 'All Transactions', title: 'All Transactions' },
    { key: 'deposit', text: 'Deposit', title: 'Deposit Transactions' },
    { key: 'withdrawal', text: 'Withdraw', title: 'Withdraw Transactions' },
  ];

  // Switching tabs restarts at page 1 immediately, so the old page number never
  // shows against the new tab's rows while the request is in flight.
  const handleNav = (nav) => {
    setActive(nav);
    setPageOffset(0);
  };

  useEffect(() => {
    // Drops the response of a request that has already been superseded — e.g.
    // the client switches tabs or pages before the previous one lands.
    let ignore = false;

    const loadTransactions = async () => {
      const result = await post('/wallet/transaction', {
        type: active,
        pageNum: 1,
        pageSize: PAGE_SIZE,
        sortField: SORT_FIELD,
        sortOrder: SORT_ORDER,
      });
      if (ignore) return;

      if (result?.status != 200 || !Array.isArray(result.data)) {
        setAllTransactions([]);
        setLoadFailed(true);
        return;
      }

      setLoadFailed(false);
      setAllTransactions([...result.data].sort(sortByNewestFirst));
      setPageOffset(0);
    };

    loadTransactions();
    return () => {
      ignore = true;
    };
  }, [active]);

  const handlePageChange = (event) => {
    setPageOffset(event.selected);
  };

  return (
    <Layout menu="transaction">
      <UserHead
        sub="Wallet Management"
        title="Transaction History"
        description="List of transactions in your account."
      >
        <ul className="nk-block-tools gx-3">
          <li>
            <Link to="/portal/deposit" className="btn btn-primary">
              <span>Deposit</span>{' '}
              <em className="icon ni ni-arrow-long-right"></em>
            </Link>
          </li>
        </ul>
      </UserHead>

      <WalletNav active="transaction" />

      <ul className="nk-nav nav nav-tabs">
        {navs.map((item, i) => (
          <li
            className={active == item.key ? 'nav-item active' : 'nav-item'}
            key={i}
          >
            <a
              className="nav-link"
              href="#"
              onClick={(e) => (e.preventDefault(), handleNav(item.key))}
            >
              {item.text}
            </a>
          </li>
        ))}
      </ul>
      <div className="nk-block nk-block-xs">
        {loadFailed && (
          <WarnAlert message="We could not load your transactions just now. Please refresh the page or try again shortly." />
        )}

        {transactions.length > 0 && (
          <div className="nk-block-head nk-block-head-sm">
            {/* <div className="nk-block-between">
            <div className="nk-block-head-content">
              <h6 className="nk-block-title">{title}</h6>
            </div>
          </div> */}
            <form action="http://localhost:8888/transactions" method="GET">
              <div
                className="search-wrap search-wrap-extend bg-lighter"
                data-search="search"
              >
                <div className="search-content">
                  <a
                    href="#"
                    className="search-back btn btn-icon toggle-search"
                    data-target="search"
                  >
                    <em className="icon ni ni-arrow-left"></em>
                  </a>
                  <input
                    type="text"
                    name="query"
                    className="form-control border-transparent form-focus-none"
                    placeholder="Search by transaction id"
                  />
                  <button className="search-submit btn btn-icon mr-1">
                    <em className="icon ni ni-search"></em>
                  </button>
                </div>
              </div>
            </form>
          </div>
        )}

        <div className="nk-odr-list is-stretch card card-bordered mb-2">
          {transactions.length > 0 &&
            transactions.map((t, i) => (
              <TransactionRow
                transaction={t}
                key={i}
                onShow={() => setTransaction(t)}
              />
            ))}
          {transactions.length == 0 && !loadFailed && (
            <div className="nk-odr-item">
              <div className="nk-odr-col">No transactions found!</div>
            </div>
          )}
        </div>
        {pageCount > 1 && (
          <ReactPaginate
            previousLabel="Previous"
            nextLabel="Next"
            pageClassName="page-item"
            pageLinkClassName="page-link"
            previousClassName="page-item"
            previousLinkClassName="page-link"
            nextClassName="page-item"
            nextLinkClassName="page-link"
            breakLabel="..."
            breakClassName="page-item"
            breakLinkClassName="page-link"
            pageCount={pageCount}
            marginPagesDisplayed={2}
            pageRangeDisplayed={5}
            onPageChange={handlePageChange}
            containerClassName="pagination"
            activeClassName="active"
            forcePage={Math.min(pageOffset, pageCount - 1)}
          />
        )}
        <div className="mt-4"></div>
      </div>

      <Dialog id="txn-details">
        {transaction?.investment && (
          <InvestmentTransaction
            transaction={transaction.investment}
            referenceId={transaction.reference_id}
          />
        )}

        {transaction?.deposit && (
          <DepositTransaction
            transaction={transaction.deposit}
            referenceId={transaction.reference_id}
          />
        )}

        {transaction?.withdrawal && (
          <WithdrawalTransaction
            transaction={transaction.withdrawal}
            referenceId={transaction.reference_id}
          />
        )}
        {transaction?.redemption && (
          <RedemptionTransaction
            transaction={transaction.redemption}
            referenceId={transaction.reference_id}
          />
        )}
        {transaction?.swap && (
          <SwapTransaction
            transaction={transaction.swap}
            referenceId={transaction.reference_id}
          />
        )}
      </Dialog>
    </Layout>
  );
}

export default Page;
